import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { usePackages } from '../../../../services/package.service';
import { useTokenPacks } from '../../../../services/tokenPack.service';
import type { AppliesTo, BillingPeriod, CampaignTarget, TargetInput } from '../types';

interface Props {
  open: boolean;
  appliesTo: AppliesTo;
  targets: CampaignTarget[];
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (targets: TargetInput[]) => void;
}

export const TargetsDialog: React.FC<Props> = ({
  open,
  appliesTo,
  targets,
  saving,
  error,
  onClose,
  onSave,
}) => {
  const { data: packages = [] } = usePackages();
  const { data: tokenPacks = [] } = useTokenPacks();
  const [packageIds, setPackageIds] = useState<Set<string>>(new Set());
  const [tokenPackIds, setTokenPackIds] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState<'' | BillingPeriod>('');

  useEffect(() => {
    if (!open) return;
    setPackageIds(new Set(targets.flatMap((t) => (t.package ? [t.package.publicId] : []))));
    setTokenPackIds(new Set(targets.flatMap((t) => (t.tokenPack ? [t.tokenPack.publicId] : []))));
    setPeriod(targets.find((t) => t.billingPeriod)?.billingPeriod ?? '');
  }, [open, targets]);

  const toggle = (set: Set<string>, id: string, apply: (next: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  };

  const save = () =>
    onSave([
      ...[...packageIds].map((packagePublicId) => ({
        packagePublicId,
        billingPeriod: period || null,
      })),
      ...[...tokenPackIds].map((tokenPackPublicId) => ({ tokenPackPublicId })),
    ]);

  const payable = packages.filter((p) => p.code !== 'FREE' && p.isActive);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Campaign targets</DialogTitle>
      <DialogContent>
        <div className="mt-2 space-y-4">
          <Alert severity="info">
            Leave everything unchecked to apply the campaign to every eligible item. Once you tick
            anything, the campaign applies only to the ticked items.
          </Alert>
          {appliesTo === 'ANY' && packageIds.size > 0 !== tokenPackIds.size > 0 && (
            <Alert severity="warning">
              {packageIds.size > 0
                ? 'Only packages are ticked, so token packs will not get this discount.'
                : 'Only token packs are ticked, so packages will not get this discount.'}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {appliesTo !== 'TOKEN_PACK' && (
            <div>
              <Typography variant="subtitle2">Packages</Typography>
              {payable.map((pkg) => (
                <FormControlLabel
                  key={pkg.publicId}
                  className="block"
                  control={
                    <Checkbox
                      size="small"
                      checked={packageIds.has(pkg.publicId)}
                      onChange={() => toggle(packageIds, pkg.publicId, setPackageIds)}
                    />
                  }
                  label={`${pkg.name} (${pkg.code})`}
                />
              ))}
              <TextField
                select
                size="small"
                label="Billing period"
                value={period}
                onChange={(e) => setPeriod(e.target.value as '' | BillingPeriod)}
                className="mt-2"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All periods</MenuItem>
                <MenuItem value="month">Monthly</MenuItem>
                <MenuItem value="year">Yearly</MenuItem>
                <MenuItem value="one_time">One time</MenuItem>
              </TextField>
            </div>
          )}
          {appliesTo !== 'PACKAGE' && (
            <div>
              <Typography variant="subtitle2">Token packs</Typography>
              {tokenPacks
                .filter((p) => p.isActive)
                .map((pack) => (
                  <FormControlLabel
                    key={pack.publicId}
                    className="block"
                    control={
                      <Checkbox
                        size="small"
                        checked={tokenPackIds.has(pack.publicId)}
                        onChange={() => toggle(tokenPackIds, pack.publicId, setTokenPackIds)}
                      />
                    }
                    label={`${pack.name} (${pack.code})`}
                  />
                ))}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save targets
        </Button>
      </DialogActions>
    </Dialog>
  );
};
